/**
 * Authorable taxi network — the data model the waypoint editor edits and the
 * ground router consumes.
 *
 * A `TaxiPlan` is one versioned scenario for a location (e.g. "Regular ops",
 * "Masters 2027"): a set of waypoint **nodes** and **edges** organized into
 * **groups**. Every element carries an `ActiveWindow` (when it is live) plus a
 * free-form `flags` bag for future config. Edges additionally carry scheduled
 * `closures`. Flipping `active.enabled` is the real-time emergency kill switch.
 *
 * Positions are in **scene feet `[x, z]`** — the same space as `FieldGraph` and
 * `ZoneGeometry` (see CoordinateService / motion/easing for the convention:
 * +X = east, +Z = south). Self-contained (no DB/React coupling) so it is
 * trivially testable, mirroring `engine/types.ts`.
 */

export type NodeRole = 'taxi' | 'apron' | 'hold_short' | 'runway_exit' | 'exit_to_taxiway' | 'gate'
export type EdgeRole = 'taxiway' | 'apron_lead' | 'runway' | 'exit' | 'temporary'
export type GroupRole = 'permanent' | 'temporary' | 'emergency_reroute'

/**
 * Reusable activation window. `enabled:false` forces the element off regardless
 * of the window (the real-time emergency-close switch). `mode:'always'` ignores
 * `from`/`until`; `mode:'window'` is live only within [from, until].
 */
export interface ActiveWindow {
  mode: 'always' | 'window'
  /** ISO timestamp, inclusive. Only meaningful when mode === 'window'. */
  from?: string | null
  /** ISO timestamp, exclusive. Only meaningful when mode === 'window'. */
  until?: string | null
  enabled: boolean
}

/** A scheduled closure of an edge: the edge is inactive within [from, until]. */
export interface ClosureWindow {
  id: string
  from: string
  until: string
  reason?: string
}

export interface TaxiNode {
  id: string
  groupId: string
  /** scene feet [x, z] */
  pos: [number, number]
  role: NodeRole
  /** optional flow / face direction, degrees (scene heading) */
  headingDeg?: number | null
  /** for exit_to_taxiway nodes — advisory departure throughput per slot */
  throughputPerSlot?: number | null
  active: ActiveWindow
  /** custom JSON flags, future-proofing */
  flags: Record<string, unknown>
}

export interface TaxiEdge {
  id: string
  groupId: string
  /** endpoint node ids */
  a: string
  b: string
  role: EdgeRole
  /** one-way taxiway (a → b only) when true */
  directed: boolean
  /** routing cost multiplier (default 1) */
  weightMult?: number
  active: ActiveWindow
  closures: ClosureWindow[]
  flags: Record<string, unknown>
}

export interface TaxiGroup {
  id: string
  name: string
  role: GroupRole
  /** display color (hex), optional */
  color?: string
  active: ActiveWindow
  flags: Record<string, unknown>
}

/**
 * One physical end of a runway (e.g. "17"). The two ends of a runway face
 * opposite directions; which end is in use for arrivals vs departures is what
 * the motion sim reads to orient final / takeoff-roll / climb.
 */
export interface RunwayEnd {
  /** end designator, e.g. "17", "35", "08L". */
  designator: string
  /** threshold position, scene feet [x, z]. */
  threshold: [number, number]
  /** displaced threshold distance down the runway, feet (default 0). */
  displacedThresholdFt?: number
  /** aircraft land toward this end (touch down here). */
  useForLanding: boolean
  /** aircraft depart from this end (start the takeoff roll here). */
  useForTakeoff: boolean
}

/**
 * An authorable runway. First-class in the plan (persisted in the JSONB graph)
 * so the editor can draw/adjust it and the field graph derives airborne legs +
 * runway-roll geometry from the configured ends instead of a synth heuristic.
 */
export interface TaxiRunway {
  id: string
  groupId: string
  /** display name, e.g. "17/35". */
  name: string
  /** the two opposing ends. */
  ends: [RunwayEnd, RunwayEnd]
  /** paved width, feet. */
  widthFt: number
  /** surface descriptor (e.g. 'asphalt' | 'concrete'). */
  surface?: string
  /** when this runway config is live (windowed so a wind shift can swap configs). */
  active: ActiveWindow
  flags: Record<string, unknown>
}

export interface TaxiPlan {
  id: string
  locationId: string
  name: string
  description?: string
  /** the baseline/regular plan for the location */
  isBaseline: boolean
  /** when this plan/scenario is the active one (advisory; switcher is manual) */
  active: ActiveWindow
  groups: TaxiGroup[]
  nodes: TaxiNode[]
  edges: TaxiEdge[]
  runways: TaxiRunway[]
  flags: Record<string, unknown>
}

/** Just the `{groups,nodes,edges,runways}` payload persisted in `taxi_plans.graph`. */
export interface TaxiGraphData {
  groups: TaxiGroup[]
  nodes: TaxiNode[]
  edges: TaxiEdge[]
  runways: TaxiRunway[]
}

// ── ID + factory helpers ─────────────────────────────────────────────────────

/** Short unique id (crypto.randomUUID where available, else a timestamped rand). */
export function makeId(prefix: string): string {
  const uuid =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
  return `${prefix}_${uuid.slice(0, 8)}`
}

/** An always-on window — the default for newly authored elements. */
export const alwaysActive = (): ActiveWindow => ({ mode: 'always', enabled: true })

/** Build a runway from two thresholds; defaults end A → landing, end B → takeoff. */
export function mkRunway(
  groupId: string,
  name: string,
  a: { designator: string; threshold: [number, number] },
  b: { designator: string; threshold: [number, number] },
  extra?: Partial<Omit<TaxiRunway, 'id' | 'groupId' | 'name' | 'ends'>> & {
    endA?: Partial<RunwayEnd>
    endB?: Partial<RunwayEnd>
  }
): TaxiRunway {
  return {
    id: makeId('rwy'),
    groupId,
    name,
    ends: [
      { designator: a.designator, threshold: a.threshold, useForLanding: true, useForTakeoff: false, ...extra?.endA },
      { designator: b.designator, threshold: b.threshold, useForLanding: false, useForTakeoff: true, ...extra?.endB },
    ],
    widthFt: extra?.widthFt ?? 150,
    surface: extra?.surface,
    active: extra?.active ?? alwaysActive(),
    flags: extra?.flags ?? {},
  }
}

// ── Activation resolution (pure) ─────────────────────────────────────────────

/** Is timestamp `atMs` inside the closed interval represented by the window? */
function withinWindow(w: ActiveWindow, atMs: number): boolean {
  if (w.mode !== 'window') return true
  const from = w.from ? Date.parse(w.from) : Number.NEGATIVE_INFINITY
  const until = w.until ? Date.parse(w.until) : Number.POSITIVE_INFINITY
  return atMs >= from && atMs < until
}

/** Is `atMs` inside any of the scheduled closures? */
function inAnyClosure(closures: ClosureWindow[] | undefined, atMs: number): boolean {
  if (!closures || closures.length === 0) return false
  for (const c of closures) {
    const from = Date.parse(c.from)
    const until = Date.parse(c.until)
    if (Number.isFinite(from) && Number.isFinite(until) && atMs >= from && atMs < until) return true
  }
  return false
}

/**
 * Is an element live at `atMs`? True when the master switch is on, the window
 * includes `atMs`, and no closure covers `atMs`. Group gating is applied
 * separately by `resolveNetworkAt`.
 */
export function isActiveAt(
  w: ActiveWindow,
  closures: ClosureWindow[] | undefined,
  atMs: number
): boolean {
  if (!w.enabled) return false
  if (!withinWindow(w, atMs)) return false
  if (inAnyClosure(closures, atMs)) return false
  return true
}

/**
 * The subset of nodes/edges live at `atMs`, already group-filtered. An element
 * is excluded if its own window is inactive OR its group is inactive at `atMs`.
 * Edges referencing a dropped node are also excluded so the router never sees a
 * dangling endpoint.
 */
export function resolveNetworkAt(plan: TaxiPlan, atMs: number): { nodes: TaxiNode[]; edges: TaxiEdge[] } {
  const groupActive = new Map<string, boolean>()
  for (const g of plan.groups) groupActive.set(g.id, isActiveAt(g.active, undefined, atMs))
  // Elements with an unknown/missing group are treated as ungated (active).
  const groupLive = (groupId: string) => groupActive.get(groupId) ?? true

  const nodes = plan.nodes.filter((n) => groupLive(n.groupId) && isActiveAt(n.active, undefined, atMs))
  const liveNodeIds = new Set(nodes.map((n) => n.id))

  const edges = plan.edges.filter(
    (e) =>
      groupLive(e.groupId) &&
      isActiveAt(e.active, e.closures, atMs) &&
      liveNodeIds.has(e.a) &&
      liveNodeIds.has(e.b)
  )
  return { nodes, edges }
}

/**
 * The active runway at `atMs` — the first whose group + own window are live,
 * giving the landing/departure end config the motion sim orients to. Returns
 * null when no runway is authored or live (caller falls back to the synth graph).
 */
export function resolveActiveRunway(plan: TaxiPlan, atMs: number): TaxiRunway | null {
  const groupActive = new Map<string, boolean>()
  for (const g of plan.groups) groupActive.set(g.id, isActiveAt(g.active, undefined, atMs))
  const groupLive = (groupId: string) => groupActive.get(groupId) ?? true
  for (const r of plan.runways ?? []) {
    if (groupLive(r.groupId) && isActiveAt(r.active, undefined, atMs)) return r
  }
  return null
}

/** Convenience: pull the persisted graph payload off a plan. */
export const toGraphData = (plan: TaxiPlan): TaxiGraphData => ({
  groups: plan.groups,
  nodes: plan.nodes,
  edges: plan.edges,
  runways: plan.runways ?? [],
})
